import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete
} from '@mui/material';
import { apiClient } from '../../lib/apiClient';

function ChurchAdmins({ user }) {
  const [churchAdmins, setChurchAdmins] = useState([]);
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [churchesData, usersData] = await Promise.all([
          apiClient.getAllChurches(),
          apiClient.getAllUsers()
        ]);

        setChurches(churchesData);
        setAllUsers(usersData.filter(u => u.role !== 'super_admin'));

        // Build church admins list
        const admins = [];
        for (const church of churchesData) {
          if (church.churchAdminIds && church.churchAdminIds.length > 0) {
            for (const adminId of church.churchAdminIds) {
              const adminUser = usersData.find(u => u.id === adminId);
              if (adminUser) {
                admins.push({
                  id: `${church.id}-${adminId}`,
                  churchId: church.id,
                  churchName: church.name,
                  userId: adminUser.id,
                  userName: adminUser.name,
                  userPhone: adminUser.phoneNumber
                });
              }
            }
          }
        }
        setChurchAdmins(admins);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  const handleAddAdmin = async () => {
    if (!selectedChurch || !selectedUser) return;

    try {
      await apiClient.addChurchAdmin(selectedChurch.id, user.id, selectedUser.id);
      // Refresh data
      const [churchesData, usersData] = await Promise.all([
        apiClient.getAllChurches(),
        apiClient.getAllUsers()
      ]);

      const admins = [];
      for (const church of churchesData) {
        if (church.churchAdminIds && church.churchAdminIds.length > 0) {
          for (const adminId of church.churchAdminIds) {
            const adminUser = usersData.find(u => u.id === adminId);
            if (adminUser) {
              admins.push({
                id: `${church.id}-${adminId}`,
                churchId: church.id,
                churchName: church.name,
                userId: adminUser.id,
                userName: adminUser.name,
                userPhone: adminUser.phoneNumber
              });
            }
          }
        }
      }
      setChurchAdmins(admins);
      setOpenDialog(false);
      setSelectedChurch(null);
      setSelectedUser(null);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRemoveAdmin = async (churchId, userId) => {
    if (!window.confirm('Are you sure you want to remove this admin?')) {
      return;
    }

    try {
      await apiClient.removeChurchAdmin(churchId, user.id, userId);
      setChurchAdmins(churchAdmins.filter(a => !(a.churchId === churchId && a.userId === userId)));
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Church Admins
        </Typography>
        <Button
          variant="contained"
          onClick={() => setOpenDialog(true)}
        >
          Add Admin
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Church Name</TableCell>
              <TableCell>Admin Name</TableCell>
              <TableCell>Phone Number</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {churchAdmins.map((admin) => (
              <TableRow key={admin.id}>
                <TableCell>{admin.churchName}</TableCell>
                <TableCell>{admin.userName}</TableCell>
                <TableCell>{admin.userPhone}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleRemoveAdmin(admin.churchId, admin.userId)}
                  >
                    Remove
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Church Admin</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Autocomplete
              options={churches}
              getOptionLabel={(option) => option.name}
              value={selectedChurch}
              onChange={(event, newValue) => setSelectedChurch(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Church" required />
              )}
            />
            <Autocomplete
              options={allUsers}
              getOptionLabel={(option) => `${option.name} (${option.phoneNumber})`}
              value={selectedUser}
              onChange={(event, newValue) => setSelectedUser(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="User" required />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddAdmin}
            variant="contained"
            disabled={!selectedChurch || !selectedUser}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ChurchAdmins;

